"use client";

import { Button, Input } from "ui";
import { useRouter } from "next/navigation";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useLocale, useTranslation } from "@sajilni/localization";
import { ArrowLeft } from "lucide-react";
import { Logo } from "@/components/Logo";

export default function ForgetPasswordForm() {
  const local = useLocale();
  const dir = local === "ar" ? "rtl" : "ltr";
  const router = useRouter();
  const { t } = useTranslation("sajilni2.0");

  const formSchema = z.object({
    email: z
      .string()
      .min(1, { message: t("errors.emailRequired") })
      .email({ message: t("errors.invalidEmail") }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  return (
    <div
      dir={dir}
      className="notranslate relative flex min-h-[calc(100vh-108px)] flex-col"
    >
      <div className="w-full">
        <Logo />
      </div>
      <div className="flex flex-1 items-center justify-center px-4 py-6 sm:px-6 md:px-8 lg:px-11">
        <div className="flex w-full flex-col items-center justify-center">
        <div className="flex h-full flex-col items-center justify-center gap-32">
          <div className="flex w-full flex-col items-start gap-3 sm:max-w-sm">
            <Button
              variant="ghost"
              className="mb-2 flex h-auto items-center !p-0 text-gray-600 hover:text-gray-900"
              onClick={() => router.back()}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              <span>{t("actions.back")}</span>
            </Button>

            <div className="text-3xl font-semibold">
              {t("titles.lostYourPassword")}
            </div>
            <div className="text-gray-600">
              {t("titles.descriptionLostPassword")}
            </div>

            <div className="flex w-full flex-col gap-2 pb-6 pt-4">
              <div className="text-sm font-medium text-gray-700">
                {t("labels.email")}
              </div>
              <Input
                className="!w-full"
                type="email"
                placeholder={t("Enter email")}
                {...form.register("email")}
              />
              {form.formState.errors.email && (
                <div className="text-sm text-red-500">
                  {form.formState.errors.email.message}
                </div>
              )}
            </div>

            <Button
              className="bg-primary w-full text-white"
              onClick={() => {
                const email = form.getValues("email");
                router.push(
                  `/verify-request?email=${encodeURIComponent(email)}`
                );
              }}
            >
              {t("titles.resetPassword")}
            </Button>
          </div>
        </div>
      </div>
      <div className="fixed bottom-4 text-sm text-gray-600 sm:bottom-6 ltr:left-4 ltr:sm:left-6 ltr:md:left-8 ltr:lg:left-11 rtl:right-4 rtl:sm:right-6 rtl:md:right-8 rtl:lg:right-11">
        {t("footer.copyright", { year: new Date().getFullYear() })}
      </div>
    </div>
    </div>
  );
}
